import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipTapEditor from '../../components/common/TipTapEditor';
import { accordionSummarySx, accordionSx } from './auditDetailShared';

interface AuditRichTextSectionProps {
  isDark: boolean;
  title: string;
  icon: React.ReactNode;
  value: string;
  dirty: boolean;
  expanded: boolean;
  placeholder: string;
  emptyLabel?: string;
  saving: boolean;
  onToggle: (_: React.SyntheticEvent, isExpanded: boolean) => void;
  onChange: (html: string) => void;
  onReset: () => void;
  onSave: () => void;
  onPasteImage?: (file: File) => Promise<string | null>;
}

const AuditRichTextSection: React.FC<AuditRichTextSectionProps> = ({
  isDark,
  title,
  icon,
  value,
  dirty,
  expanded,
  placeholder,
  emptyLabel = 'Noch nicht hinterlegt',
  saving,
  onToggle,
  onChange,
  onReset,
  onSave,
  onPasteImage,
}) => (
  <Accordion expanded={expanded} onChange={onToggle} sx={accordionSx(isDark)}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {!value && (
        <Chip
          label={emptyLabel}
          size="small"
          sx={{
            ml: 1,
            fontWeight: 500,
            fontSize: '0.65rem',
            height: 20,
            bgcolor: isDark ? 'rgba(255,152,0,0.12)' : 'rgba(255,152,0,0.08)',
            color: '#ff9800',
            border: '1px solid rgba(255,152,0,0.3)',
          }}
        />
      )}
      {dirty && (
        <Chip
          label="Ungespeichert"
          size="small"
          sx={{
            ml: 1,
            fontWeight: 500,
            fontSize: '0.65rem',
            height: 20,
            bgcolor: isDark ? 'rgba(66,165,245,0.12)' : 'rgba(66,165,245,0.08)',
            color: '#42a5f5',
            border: '1px solid rgba(66,165,245,0.3)',
          }}
        />
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ px: 3, py: 2 }}>
      <TipTapEditor
        content={value || ''}
        onChange={onChange}
        editable={true}
        placeholder={placeholder}
        minHeight={200}
        onPasteImage={onPasteImage}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1.5 }}>
        <Button variant="outlined" size="small" disabled={!dirty || saving} onClick={onReset}>
          Verwerfen
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!dirty || saving}
          onClick={onSave}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Speichert...' : 'Speichern'}
        </Button>
      </Box>
    </AccordionDetails>
  </Accordion>
);

export default AuditRichTextSection;
